"use client";

import dynamic from "next/dynamic";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { preloadDevToolboxModules } from "@/lib/devToolbox/bindScenarioListener";
import { isDevToolboxEnabled } from "@/lib/devToolbox/isDevToolboxEnabled";
import Header from "@/components/layout/Header";
import MobileHeader from "@/components/layout/MobileHeader";
import MobileMenu from "@/components/layout/MobileMenu";
import MobileTabPageTransition from "@/components/layout/MobileTabPageTransition";
import Footer from "@/components/layout/Footer";
import AppProviders from "@/components/providers/AppProviders";
import type { ColorModePreference } from "@/lib/design-system/theme";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PwaPostInstallPushPrompt from "@/components/PwaPostInstallPushPrompt";
import PWAUpdatePrompt from "@/components/PWAUpdatePrompt";
import CuoralWidget from "@/components/CuoralWidget";
import ProductHelpChrome from "@/components/feedback/ProductHelpChrome";
import NotificationSetupBanner from "@/components/notifications/NotificationSetupBanner";

const DevToolboxActiveBanner =
  process.env.NODE_ENV === "development"
    ? dynamic(() => import("@/components/dev/DevToolboxActiveBanner"), {
        ssr: false,
      })
    : () => null;

const DevToolboxShell =
  process.env.NODE_ENV === "development"
    ? dynamic(() => import("@/components/dev/DevToolboxShell"), { ssr: false })
    : () => null;

const DevToolboxNewUserWalkthroughPanel =
  process.env.NODE_ENV === "development"
    ? dynamic(
        () => import("@/components/dev/DevToolboxNewUserWalkthroughPanel"),
        { ssr: false },
      )
    : () => null;
import { NotificationSetupBannerProvider } from "@/contexts/NotificationSetupBannerContext";
import { SellerListingAttentionProvider } from "@/contexts/SellerListingAttentionContext";

import { setupProductionServiceWorkerLifecycle } from "@/lib/pwa/service-worker-client";

const MARKETING_FOOTER_ROUTES = new Set([
  "/",
  "/about-us",
  "/faq",
  "/terms",
  "/trust-safety",
  "/contact-us",
  "/locations",
]);

export default function ClientLayout({
  children,
  initialColorModePreference = "system",
}: {
  children: React.ReactNode;
  initialColorModePreference?: ColorModePreference;
}) {
  const pathname = usePathname();

  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    preloadDevToolboxModules();
    void import("@/components/dev/DevToolboxShell");
    void import("@/components/dev/DevToolboxActiveBanner");
  }, []);

  useEffect(() => setupProductionServiceWorkerLifecycle(), []);

  const currentPath = pathname || "/";
  const showFooter = MARKETING_FOOTER_ROUTES.has(currentPath);

  return (
    <AppProviders initialColorModePreference={initialColorModePreference}>
      <SellerListingAttentionProvider>
        <NotificationSetupBannerProvider>
          <Header />
          <MobileHeader />
          <NotificationSetupBanner />
          <main className="min-h-screen mobile-shell-content--with-nav">
            <MobileTabPageTransition>{children}</MobileTabPageTransition>
          </main>
        </NotificationSetupBannerProvider>
        {showFooter ? <Footer /> : null}
        <MobileMenu />
        <PWAUpdatePrompt withBottomNavOffset />
        <PWAInstallPrompt withBottomNavOffset />
        <PwaPostInstallPushPrompt />
        {/* D1: one Help FAB (feedback + Cuoral + tour). TourFloatingButton demoted. */}
        <ProductHelpChrome />
        <CuoralWidget />
        {isDevToolboxEnabled() ? (
          <>
            <DevToolboxActiveBanner />
            <DevToolboxNewUserWalkthroughPanel />
            <DevToolboxShell />
          </>
        ) : null}
      </SellerListingAttentionProvider>
    </AppProviders>
  );
}
